import { requireElement } from "@modules/bookmarks/html-elements"
import { htmlToElement } from "@modules/bookmarks/html-parser"
import { TagShowOptionsEvent } from "@modules/bookmarks/tags/Tag"
import type { TagEvent, TagInstance } from "@modules/bookmarks/tags/Tag"
import { consumeEvent } from "@modules/util/events"

export type OptionEventPayload = {
    tag: TagInstance
}
export type OptionEvent = CustomEvent<OptionEventPayload>

export const TagDeleteOptionClickedEvent = "bookmarks.tag:option-delete" as const
export const TagRenameOptionClickedEvent = "bookmarks.tag:option-rename" as const

const dropdownElement: HTMLDivElement = htmlToElement<HTMLDivElement>(`
    <div class="dropdown-anchor">
        <ul class="options-list" hidden>
            <li class="tag-option" data-action="delete">
                <svg viewBox="0 0 20 20"><path d="M7.5 0h3.75c0.688 0 1.25 0.563 1.25 1.25v1.25h2.5c0.688 0 1.25 0.563 1.25 1.25v1.25c0 0.687-0.563 1.25-1.25 1.25v12.5c0 0.687-0.563 1.25-1.25 1.25h-8.75c-0.687 0-1.25-0.563-1.25-1.25v-12.5c-0.688 0-1.25-0.563-1.25-1.25v-1.25c0-0.688 0.563-1.25 1.25-1.25h2.5v-1.25c0-0.688 0.563-1.25 1.25-1.25zM8.75 8.75v7.5h1.25v-7.5h-1.25zM11.25 8.75v7.5h1.25v-7.5h-1.25zM6.25 8.75v7.5h1.25v-7.5h-1.25zM13.75 6.25h-8.75v12.5h8.75v-12.5zM11.25 2.5v-1.25h-3.75v1.25h3.75zM3.75 3.75v1.25h11.25v-1.25h-11.25z"></path></svg>
                Löschen
            </li>
            <li class="tag-option" data-action="rename">
                <svg viewBox="0 0 32 32"><path d="M24.929 1.214c0.786-0.786 1.857-1.214 2.929-1.214s2.143 0.429 2.929 1.214v0 0 0c0.786 0.786 1.214 1.857 1.214 2.929s-0.429 2.143-1.214 2.929v0 0 0l-20.071 20.071-5.929-5.857 20.143-20.071zM27.857 1.357c-0.714 0-1.429 0.286-2 0.857l-19.143 19.071 4 3.929 19.071-19.071c0.571-0.571 0.857-1.286 0.857-2s-0.286-1.429-0.857-1.929v0 0 0c-0.5-0.571-1.214-0.857-1.929-0.857zM8.857 29l-0.214 0.214c-2.857 0.929-5.786 1.857-8.643 2.786l2.857-8.714 0.571-0.643 5.929 5.857-0.5 0.5zM2.143 29.857l5-1.571-3.357-3.429-1.643 5z"></path></svg>
                Umbenennen
            </li>
        </ul>
    </div>
`)
const optionsList: HTMLUListElement | null = requireElement<HTMLUListElement>(".options-list", dropdownElement)
const deleteOption: HTMLUListElement | null = requireElement<HTMLUListElement>(
    'li[data-action="delete"]',
    dropdownElement,
)
const renameOption: HTMLUListElement | null = requireElement<HTMLUListElement>(
    'li[data-action="rename"]',
    dropdownElement,
)

let currentDropdownTag: TagInstance

const attachToTag = (tag: TagInstance): void => {
    currentDropdownTag = tag

    requestAnimationFrame((): void => {
        tag.getTagRootElement().appendChild(dropdownElement)
        optionsList?.removeAttribute("hidden")
    })
}

const triggerDeleteTagOptionEvent = (): void => {
    dropdownElement.dispatchEvent(
        new CustomEvent(TagDeleteOptionClickedEvent, {
            bubbles: true,
            detail: { tag: currentDropdownTag } as OptionEventPayload,
        }),
    )
}

const triggerRenameTagOptionEvent = (): void => {
    dropdownElement.dispatchEvent(
        new CustomEvent(TagRenameOptionClickedEvent, {
            bubbles: true,
            detail: { tag: currentDropdownTag } as OptionEventPayload,
        }),
    )
}

const closeOptions = (): void => {
    optionsList?.setAttribute("hidden", "")
}

export const registerDropdownEvents = (): void => {
    document.addEventListener(
        TagShowOptionsEvent,
        consumeEvent((tagEvent: TagEvent): void => attachToTag(tagEvent.detail.tag)),
    )
    deleteOption?.addEventListener(
        "click",
        consumeEvent((): void => triggerDeleteTagOptionEvent()),
    )
    renameOption?.addEventListener(
        "click",
        consumeEvent((): void => triggerRenameTagOptionEvent()),
    )
    document.addEventListener("click", () => closeOptions(), { passive: true })
}

// Register Custom Events
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface DocumentEventMap {
        [TagDeleteOptionClickedEvent]: OptionEvent
        [TagRenameOptionClickedEvent]: OptionEvent
    }
    interface HTMLElementEventMap {
        [TagDeleteOptionClickedEvent]: OptionEvent
        [TagRenameOptionClickedEvent]: OptionEvent
    }
}
